/**
 * SpatialCardLayer — global desktop-only cursor → spatial Web Audio bridge.
 *
 * Atmos-INSPIRED (NOT Dolby Atmos). When a fine-pointer cursor enters any
 * element marked `data-spatial-card="true"`, we open one quiet "trace" voice
 * via audioBus.startCardSpatial; pointer movement is throttled through rAF
 * and forwarded as { x, y, dist, velocity } to audioBus.updateCardSpatial.
 * On leave / route change / blur / visibility hidden / mode-off we tear it
 * down. No nodes are created on mousemove. No Dolby branding may appear.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { audioBus } from "@/lib/audioBus";
import { isCoarsePointer, prefersReduced, supportsHover } from "@/hooks/mediaQueries";

let cardSeq = 0;
const idMap = new WeakMap<Element, string>();
function idFor(el: Element): string {
  let id = idMap.get(el);
  if (!id) { id = `sc-${++cardSeq}`; idMap.set(el, id); }
  return id;
}

const SpatialCardLayer = () => {
  const location = useLocation();

  // Cleanup on route change (cards unmount; ensure no straggling nodes).
  useEffect(() => {
    audioBus.cleanupAllSpatialCards();
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!supportsHover() || isCoarsePointer() || prefersReduced()) return;

    let activeEl: Element | null = null;
    let activeId: string | null = null;
    let activeRect: DOMRect | null = null;
    let lastX = 0, lastY = 0, lastT = 0;
    let pendingX = 0, pendingY = 0, hasPending = false;
    let rafId = 0;

    const flush = () => {
      rafId = 0;
      if (!hasPending || !activeEl || !activeId || !activeRect) return;
      hasPending = false;
      const x = pendingX, y = pendingY;
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      const lx = (x - activeRect.left) / Math.max(1, activeRect.width);
      const ly = (y - activeRect.top) / Math.max(1, activeRect.height);
      const dx = lx - 0.5, dy = ly - 0.5;
      const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 1.6);
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const v = Math.min(1, Math.hypot(x - lastX, y - lastY) / dt / 2.2);
      lastX = x; lastY = y; lastT = now;
      audioBus.updateCardSpatial(activeId, {
        x: x / vw,
        y: y / vh,
        dist,
        velocity: v,
      });
    };

    const onPointerOver = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const target = e.target as Element | null;
      const card = target?.closest?.('[data-spatial-card="true"]') as Element | null;
      if (!card || card === activeEl) return;
      // Leave previous
      if (activeEl && activeId) audioBus.stopCardSpatial(activeId);
      activeEl = card;
      activeId = idFor(card);
      activeRect = card.getBoundingClientRect();
      lastX = e.clientX; lastY = e.clientY; lastT = performance.now();
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      const lx = (e.clientX - activeRect.left) / Math.max(1, activeRect.width);
      const ly = (e.clientY - activeRect.top) / Math.max(1, activeRect.height);
      const dx = lx - 0.5, dy = ly - 0.5;
      const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 1.6);
      audioBus.startCardSpatial(activeId, {
        x: e.clientX / vw,
        y: e.clientY / vh,
        dist,
        velocity: 0,
      });
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!activeEl) return;
      pendingX = e.clientX; pendingY = e.clientY; hasPending = true;
      if (!rafId) rafId = requestAnimationFrame(flush);
    };

    const onPointerOut = (e: PointerEvent) => {
      if (!activeEl || !activeId) return;
      const related = e.relatedTarget as Element | null;
      if (related && activeEl.contains(related)) return;
      audioBus.stopCardSpatial(activeId);
      activeEl = null; activeId = null; activeRect = null;
    };

    // Recompute bounds on scroll/resize so the localX/Y mapping stays accurate.
    const onScrollOrResize = () => {
      if (activeEl) activeRect = activeEl.getBoundingClientRect();
    };

    window.addEventListener("pointerover", onPointerOver, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerout", onPointerOut, { passive: true });
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener("pointerover", onPointerOver);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerout", onPointerOut);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafId) cancelAnimationFrame(rafId);
      audioBus.cleanupAllSpatialCards();
    };
  }, []);

  return null;
};

export default SpatialCardLayer;
